import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider } from "@/auth";
import { LangProvider } from "@/lang";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

import Home from "@/pages/Home";
import Products from "@/pages/Products";
import Category from "@/pages/Category";
import ProductDetail from "@/pages/ProductDetail";
import ChiSiamo from "@/pages/ChiSiamo";
import Cataloghi from "@/pages/Cataloghi";

import Contatti from "@/pages/Contatti";
import Login from "@/pages/Login";
import RichiediAccesso from "@/pages/RichiediAccesso";
import Portal from "@/pages/Portal";
import { VerificaEmail, Decisione, ImpostaPassword, PasswordDimenticata } from "@/pages/AreaRiservataFlow";
import ChatWidget from "@/components/ChatWidget";
import { CompareProvider } from "@/components/CompareContext";
import CompareBar from "@/components/CompareBar";
import Compare from "@/pages/Compare";
import LegalPage from "@/pages/LegalPage";
import { DatasheetProvider } from "@/components/DatasheetContext";
import GestApp from "@/gestione/GestApp";

function LenisWrap({ children }) {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: false,
    });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
  return children;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
      }, 80);
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [pathname, hash]);
  return null;
}

function Shell() {
  const { pathname } = useLocation();
  const isGestione = pathname.startsWith("/gestione");
  const isReserved = ["/portale", "/login", "/richiedi-accesso", "/area-riservata"].some((p) => pathname.startsWith(p));
  const hideChrome = isGestione || isReserved;
  return (
    <>
      {!hideChrome && <Navbar />}
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/prodotti" element={<Products />} />
        <Route path="/prodotti/p/:productSlug" element={<ProductDetail />} />
        <Route path="/prodotti/:catSlug" element={<Category />} />
        <Route path="/confronta" element={<Compare />} />
        <Route path="/privacy" element={<LegalPage kind="privacy" />} />
        <Route path="/cookie" element={<LegalPage kind="cookie" />} />
        <Route path="/cataloghi" element={<Cataloghi />} />
        <Route path="/chi-siamo" element={<ChiSiamo />} />
        <Route path="/contatti" element={<Contatti />} />
        <Route path="/login" element={<Login />} />
        <Route path="/richiedi-accesso" element={<RichiediAccesso />} />
        <Route path="/portale" element={<Portal />} />
        <Route path="/area-riservata" element={<Portal />} />
        <Route path="/area-riservata/verifica" element={<VerificaEmail />} />
        <Route path="/area-riservata/decisione" element={<Decisione />} />
        <Route path="/area-riservata/imposta-password" element={<ImpostaPassword />} />
        <Route path="/area-riservata/password-dimenticata" element={<PasswordDimenticata />} />
        <Route path="/gestione/*" element={<GestApp />} />
      </Routes>
      {!hideChrome && <Footer />}
      {!hideChrome && <CompareBar />}
      {!isGestione && !isReserved && <ChatWidget />}
    </>
  );
}

function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <CompareProvider>
        <BrowserRouter>
        <DatasheetProvider>
          <LenisWrap>
            <Shell />
            <Toaster
              position="bottom-center"
              theme="light"
              toastOptions={{
                style: {
                  background: "#ffffff",
                  border: "1px solid #d2d2d7",
                  color: "#1d1d1f",
                  fontSize: "14px",
                  borderRadius: "12px",
                },
              }}
            />
          </LenisWrap>
        </DatasheetProvider>
        </BrowserRouter>
        </CompareProvider>
      </AuthProvider>
    </LangProvider>
  );
}

export default App;
